import { HTTPError } from 'ky';
import * as z from 'zod/mini';

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function handleApiError(error: unknown): Promise<never> {
  if (error instanceof HTTPError) {
    const status = error.response.status;
    let body: { error?: string } = {};
    
    try {
      body = await error.response.json();
    } catch (jsonError) {
      // Response is not JSON, use status text
      throw new ApiError(
        `HTTP ${status}: ${error.response.statusText || 'Request failed'}`,
        status,
        { originalError: jsonError }
      );
    }
    
    throw new ApiError(
      body.error || `HTTP ${status} error`,
      status,
      body
    );
  }
  
  if (error instanceof z.core.$ZodError) {
    throw new ApiError(
      'Invalid API response format',
      undefined,
      error.issues
    );
  }
  
  throw error;
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function formatApiError(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }
  
  if (error instanceof z.core.$ZodError) {
    return error.issues
      .map(issue => `${issue.path.join('.')}: ${issue.message}`)
      .join(', ');
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unknown error occurred';
}